import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useHydrateAtoms } from 'jotai/utils';
import { ReactNode } from 'react';
import { httpStatus, requestSignature } from '../util/datadogDataLayer';
import { trackEvent } from '../util/datadogTracking';

// Datadog metrics-only error attributes (FD-3117). The cache `onError` handlers
// below emit a RUM event and return — never swallowing or altering error
// propagation, so react-query retry/reject behavior and Sentry capture (in the
// axios interceptor) are unchanged. `key_root` is the first key segment,
// kept low-cardinality and free of entity IDs. Params are structural so any
// react-query Query/Mutation shape is accepted without generic friction.

// Object-literal `type` carries the index signature `trackEvent` requires.
type CacheErrorAttrs = {
  key_root: string;
  status: number | 'unknown';
};

export function queryErrorAttrs(error: unknown, query: { queryKey?: readonly unknown[] }): CacheErrorAttrs {
  return {
    key_root: String(query.queryKey?.[0] ?? 'unknown'),
    status: httpStatus(error),
  };
}

export function mutationErrorAttrs(
  error: unknown,
  mutation: { options: { mutationKey?: readonly unknown[] } }
): CacheErrorAttrs {
  // Most useMutation call sites don't set a mutationKey, so fall back to the
  // failing request's "METHOD /scrubbed/path" — otherwise nearly every
  // mutation.failed event would report key_root:'unknown' and be
  // unattributable. (Event name mutation.failed vs query.failed disambiguates
  // the shared facet.)
  const mutationKey = mutation.options.mutationKey?.[0];
  return {
    key_root: mutationKey != null ? String(mutationKey) : requestSignature(error),
    status: httpStatus(error),
  };
}

// Emission is wrapped so a throw from the Datadog SDK can never escape react-query's
// error handling. The builders only read the error/key, but addAction is third-party.
function safeTrack(event: 'query.failed' | 'mutation.failed', attrs: CacheErrorAttrs): void {
  try {
    trackEvent(event, attrs);
  } catch {
    // swallow — instrumentation must not affect error propagation
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // App-wide defaults. Individual hooks may override these per query.
      // staleTime 1m cuts redundant refetches; window-focus refetching is off
      // by default (hooks that need it, e.g. document lists, opt back in).
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => safeTrack('query.failed', queryErrorAttrs(error, query)),
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) =>
      safeTrack('mutation.failed', mutationErrorAttrs(error, mutation)),
  }),
});

export function HydrateAtoms({ children }: { children: ReactNode }) {
  useHydrateAtoms(new Map([[queryClientAtom, queryClient]]));
  return children;
}
